import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getMembershipLabel, getMembershipColor } from './membershipTypes';

function MembershipApplications({ onMemberCreated }) {
    const { authFetch } = useAuth();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [convertingId, setConvertingId] = useState(null);

    useEffect(() => {
        fetchApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchApplications = async () => {
        setLoading(true);
        try {
            const res = await authFetch('/api/membership-applications');
            const data = await res.json();
            setApplications(data.data || []);
        } catch (err) {
            setError('Fehler beim Laden der Antraege');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            const res = await authFetch(`/api/membership-applications/${id}/status`, {
                method: 'PUT',
                body: { status }
            });

            if (res.ok) {
                fetchApplications();
            } else {
                alert('Fehler beim Aktualisieren');
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        }
    };

    const deleteApplication = async (id) => {
        if (!window.confirm('Antrag wirklich loeschen?')) return;

        try {
            const res = await authFetch(`/api/membership-applications/${id}`, {
                method: 'DELETE'
            });

            if (res.ok) {
                fetchApplications();
            } else {
                alert('Fehler beim Loeschen');
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        }
    };

    const convertToMember = async (id) => {
        if (!window.confirm('Aus diesem Antrag ein Mitglied anlegen? Danach kannst du Zugangsdaten vergeben.')) return;

        setConvertingId(id);
        try {
            const res = await authFetch(`/api/membership-applications/${id}/create-member`, {
                method: 'POST'
            });
            const data = await res.json();

            if (res.ok) {
                await fetchApplications();
                if (onMemberCreated) {
                    onMemberCreated(data.data.member_id);
                }
            } else {
                alert('Fehler: ' + (data.error || 'Umwandlung fehlgeschlagen'));
            }
        } catch (err) {
            alert('Fehler: ' + err.message);
        } finally {
            setConvertingId(null);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            neu: 'bg-warning text-dark',
            bearbeitet: 'bg-info',
            umgewandelt: 'bg-success',
            abgelehnt: 'bg-danger'
        };
        const labels = {
            neu: 'Neu',
            bearbeitet: 'In Bearbeitung',
            umgewandelt: 'Umgewandelt',
            abgelehnt: 'Abgelehnt'
        };
        return (
            <span className={`badge ${badges[status] || 'bg-secondary'}`}>
                {labels[status] || status}
            </span>
        );
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    const pendingCount = applications.filter(a => a.status === 'neu').length;

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Laden...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    Mitgliedschaftsantraege
                    {pendingCount > 0 && (
                        <span className="badge bg-warning text-dark ms-2">{pendingCount} neu</span>
                    )}
                </h4>
                <button className="btn btn-outline-primary btn-sm" onClick={fetchApplications}>
                    Aktualisieren
                </button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {applications.length === 0 ? (
                <div className="alert alert-info">Keine Mitgliedschaftsantraege vorhanden.</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead className="table-light">
                            <tr>
                                <th>Status</th>
                                <th>Kind</th>
                                <th>Erziehungsberechtigte/r</th>
                                <th>Kontakt</th>
                                <th>Mitgliedschaft</th>
                                <th>Kurswunsch</th>
                                <th>Eingegangen am</th>
                                <th>Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map(app => (
                                <tr key={app.id}>
                                    <td>{getStatusBadge(app.status)}</td>
                                    <td>
                                        {app.first_name} {app.last_name}
                                        {app.birth_date && (
                                            <><br /><small className="text-muted">geb. {app.birth_date}</small></>
                                        )}
                                    </td>
                                    <td>
                                        {[app.parent_salutation, app.parent_first_name, app.parent_last_name].filter(Boolean).join(' ')}
                                    </td>
                                    <td>
                                        <a href={`mailto:${app.email}`}>{app.email}</a>
                                        {app.phone && <><br /><small>{app.phone}</small></>}
                                    </td>
                                    <td>
                                        <span
                                            className="badge"
                                            style={{ backgroundColor: getMembershipColor(app.membership_type) }}
                                        >
                                            {getMembershipLabel(app.membership_type)}
                                        </span>
                                    </td>
                                    <td>
                                        {app.wunschtag || app.wunschkurs ? (
                                            <>
                                                {app.wunschtag}
                                                {app.wunschtag && app.wunschkurs && <br />}
                                                {app.wunschkurs}
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td>
                                        <small>{formatDateTime(app.created_at)}</small>
                                    </td>
                                    <td>
                                        <div className="btn-group btn-group-sm">
                                            {app.status !== 'umgewandelt' && (
                                                <button
                                                    className="btn btn-success"
                                                    onClick={() => convertToMember(app.id)}
                                                    disabled={convertingId === app.id}
                                                    title="Zu Mitglied machen"
                                                >
                                                    {convertingId === app.id ? '...' : 'Zu Mitglied machen'}
                                                </button>
                                            )}
                                            {app.status === 'neu' && (
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => updateStatus(app.id, 'abgelehnt')}
                                                    title="Ablehnen"
                                                >
                                                    Ablehnen
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => deleteApplication(app.id)}
                                                title="Loeschen"
                                            >
                                                Loeschen
                                            </button>
                                        </div>
                                        {(app.school || app.interests || app.allergies) && (
                                            <div className="mt-2">
                                                {app.school && <small className="text-muted d-block"><strong>Schule:</strong> {app.school}</small>}
                                                {app.interests && <small className="text-muted d-block"><strong>Interessen:</strong> {app.interests}</small>}
                                                {app.allergies && <small className="text-muted d-block"><strong>Allergien:</strong> {app.allergies}</small>}
                                            </div>
                                        )}
                                        {app.sepa_iban && (
                                            <div className="mt-2">
                                                <small className="text-muted d-block"><strong>SEPA-Kontoinhaber:</strong> {app.sepa_kontoinhaber}</small>
                                                <small className="text-muted d-block"><strong>SEPA-IBAN:</strong> {app.sepa_iban}</small>
                                                {app.sepa_bic && <small className="text-muted d-block"><strong>SEPA-BIC:</strong> {app.sepa_bic}</small>}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default MembershipApplications;
